import axios from 'axios'
import { toast } from 'react-toastify'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { PER_PAGE_DEFAULT } from '../../misc/Constants'
import InvitedTable from './components/InvitedTable'
import MembersSearchInput from './components/MembersSearchInput'
import TablePlaceholder from './components/TablePlaceholder'

import classes from './InvitedPage.module.scss'

import { deserialize } from '../../utility/serialization'
import { debounce } from '../../utility/debounceHelper'
import { requestGetAllStudents } from '../../actions/communitiesActions'
import { reinviteFamily } from '../../actions/familyActions'


const InvitedPage = () => {
  const { loadedInvitedData } = useOutletContext()
  const abortController = useRef(null)
  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const [students, setStudents] = useState(loadedInvitedData?.students)
  const [searchText, setSearchText] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const fetchInvited = useCallback(
    ({ search }) =>
      requestGetAllStudents({
        communityId,
        page: 1,
        perPage: PER_PAGE_DEFAULT,
        search,
        status: 'invited',
        signal: abortController.current?.signal,
      })
        .then(response => {
          const data = deserialize(response.data)
          setStudents(data.students)
        }),
    [communityId]
  )

  const handleResendInvitation = useCallback((studentId) => {
    reinviteFamily(studentId)
      .then(() => toast.success('Invitation resent successfully', { position: toast.POSITION.TOP_RIGHT }))
      .catch((error) => {
        if (error.response?.status === 503) {
          toast.error('Resending invitations is currently unavailable.', { position: toast.POSITION.TOP_RIGHT })
        } else {
          toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT })
        }
      })
  }, [])

  const updateInvited = useCallback(
    (params) => {
      setIsLoading(true)
      fetchInvited({ search: searchText, ...params })
        .catch((res) => {
          if (!axios.isCancel(res)) {
            toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT })
          }
        })
        .finally(() => { setIsLoading(false) })
    },
    [fetchInvited, searchText]
  )

  const debouncedSearch = useMemo(
    () => debounce((search) => updateInvited({ search })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleSearch = (e) => {
    setSearchText(e.target.value)
    debouncedSearch(e.target.value)
  }

  return (
    <main className={classes.wrapper}>
      <header className='py-3 d-flex align-items-center'>
        <div className={classes.searchWrapper}>
          <MembersSearchInput placeholder='Search for Students' value={searchText} onChange={handleSearch} />
        </div>
      </header>
      {students?.length === 0 ? (
        <TablePlaceholder message='No Invitations' />
      ) : (
        <InvitedTable students={students} isLoading={isLoading} onResendInvite={handleResendInvitation} />
      )}
    </main>
  )
}

export default InvitedPage
